import { z } from "zod";

/**
 * The most decimal places a Number custom attribute is shown and saved with, regardless of the concept's
 * `numericDecimalPlaces` (MDOT-25848 acceptance criteria).
 */
export const CUSTOM_ATTRIBUTE_MAX_DECIMAL_PLACES = 13;

export const CUSTOM_ATTRIBUTE_DECIMALS_MESSAGE = `Maximum ${CUSTOM_ATTRIBUTE_MAX_DECIMAL_PLACES} decimal places`;

/**
 * True when `value` has more than {@link CUSTOM_ATTRIBUTE_MAX_DECIMAL_PLACES} decimal places, counted on
 * its 15 significant digits — the most a double holds reliably — so binary noise (`0.1 + 0.2`) is not
 * counted, exponent notation included. Non-finite → false.
 */
export function exceedsCustomAttributeDecimals(value: number): boolean {
	if (!Number.isFinite(value)) {
		return false;
	}
	const [mantissa, exponent = "0"] = String(Number(value.toPrecision(15))).split("e");
	const fractionDigits = mantissa.split(".")[1]?.length ?? 0;
	return fractionDigits - Number(exponent) > CUSTOM_ATTRIBUTE_MAX_DECIMAL_PLACES;
}

type DecimalCapValues = Array<{ numberValue?: number | null } | undefined> | undefined;

/**
 * Zod `superRefine` for a form's `customAttributes`: flags every entry whose `numberValue` has more than
 * {@link CUSTOM_ATTRIBUTE_MAX_DECIMAL_PLACES} decimal places.
 */
export function refineCustomAttributeDecimals(values: DecimalCapValues, ctx: z.RefinementCtx): void {
	values?.forEach((value, index) => {
		if (value?.numberValue != null && exceedsCustomAttributeDecimals(value.numberValue)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: [index, "numberValue"],
				message: CUSTOM_ATTRIBUTE_DECIMALS_MESSAGE,
			});
		}
	});
}
